import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  InputAdornment,
  Divider,
} from '@mui/material';

import {
  Send as SendIcon,
  EmojiEmotions as EmojiIcon,
  AttachFile as FileIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const ChatReservation = ({ conversation }) => {
  const { user } = useAuth();

  const rolePath = user?.role === 3 ? 'intervenant' : 'admin';

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationInfo, setConversationInfo] = useState(null);
  const scrollRef = useRef(null);

  const conversationId = conversation?.id_conv;

  // =========================
  // MARK AS READ
  // =========================
const markAsRead = async () => {
  if (!conversationId) return;

  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/conversation/read/As/read`,
      { id_conv: conversationId },
     
    );
  } catch (err) {
    console.error('MarkAsRead error', err);
  }
};

  // =========================
  // FETCH CONVERSATION
  // =========================
  const fetchConversation = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/conversation/${conversationId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
const data= res.data;
// 🔥 IMPORTANT : header data
    setConversationInfo(data);
      const apiMessages = res.data.messages || [];

      const formatted = apiMessages.map((msg) => {
        const isClient = msg.sender_role === 'client';

        return {
          id: msg.id,
          text: msg.message,
          sender: msg.sender_role,
          align: isClient ? 'left' : 'right',
          time: new Date(msg.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
        };
      });

      setMessages(formatted);
    } catch (err) {
      console.error('Erreur fetch conversation', err);
    }
  };

  // =========================
  // INIT
  // =========================
  useEffect(() => {
    if (!conversationId) return;

    fetchConversation();
    markAsRead(); // 👈 IMPORTANT (clear badge)
  }, [conversationId]);

  // =========================
  // AUTO SCROLL
  // =========================
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // =========================
  // SEND MESSAGE
  // =========================
  const handleSendMessage = async () => {
    if (!inputValue.trim() || !conversationId) return;

    const tempMessage = {
      id: Date.now(),
      text: inputValue,
      sender: user?.role === 3 ? 'intervenant' : 'admin',
      align: 'right',
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };

    setMessages((prev) => [...prev, tempMessage]);

    const messageToSend = inputValue;
    setInputValue('');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/${rolePath}/message/send`,
        {
          id_conv: conversationId,
          message: messageToSend,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // refresh unread state (important UX)
      markAsRead();
    } catch (err) {
      console.error('Erreur envoi message', err);

      setMessages((prev) => prev.slice(0, -1));

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: err?.response?.data?.message || 'Message refusé par le serveur',
          sender: 'system',
          align: 'center',
          time: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
      ]);
    }
  };

  // =========================
  // UI
  // =========================
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '450px',
        bgcolor: '#FFF',
        borderRadius: '16px',
        border: '1px solid #E0E5F2',
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#F4F7FE',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 35, height: 35, bgcolor: '#4318FF' }}>
            {conversation?.clients?.[0]?.prenom?.charAt(0) || 'C'}
          </Avatar>

          <Box>
            <Typography fontWeight={700}>
              Discussion avec {conversationInfo?.clients?.[0]?.prenom}{' '}
              
            </Typography>

            <Typography variant="caption" color="success.main">
              ● En ligne
            </Typography>
          </Box>

          {/* 🔴 BADGE PROPRE ICI */}
          {(conversationInfo?.conversation?.unread_count ?? 0) > 0 && (
            <Box
              sx={{
                background: 'red',
                color: '#fff',
                borderRadius: '50%',
                minWidth: 22,
                height: 22,
                px: 0.8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                ml: 1,
              }}
            >
              {conversationInfo?.conversation?.unread_count}
            </Box>
          )}
        </Box>

        <IconButton size="small">
          <MoreVertIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* MESSAGES */}
      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          p: 2,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: '#FBFCFF',
        }}
      >
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              alignSelf:
                msg.sender === 'system'
                  ? 'center'
                  : msg.align === 'right'
                    ? 'flex-end'
                    : 'flex-start',
              maxWidth: '75%',
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: '12px',
                bgcolor:
                  msg.sender === 'system'
                    ? '#FFF3CD'
                    : msg.align === 'right'
                      ? '#4318FF'
                      : '#E9EDF7',
                color:
                  msg.sender === 'system'
                    ? '#856404'
                    : msg.align === 'right'
                      ? '#FFF'
                      : '#1B2559',
                textAlign: msg.sender === 'system' ? 'center' : 'left',
              }}
            >
              <Typography variant="body2">{msg.text}</Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                textAlign: 'center',
                color: '#A3AED0',
              }}
            >
              {msg.time}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* INPUT */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Écrire un message..."
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: '#F4F7FE',
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton size="small">
                  <EmojiIcon sx={{ color: '#A3AED0' }} />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small">
                  <FileIcon sx={{ color: '#A3AED0' }} />
                </IconButton>

                <IconButton
                  onClick={handleSendMessage}
                  sx={{
                    bgcolor: '#4318FF',
                    color: '#FFF',
                    ml: 1,
                    '&:hover': { bgcolor: '#3311CC' },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Paper>
  );
};

export default ChatReservation;
